import dayjs from 'dayjs';
import dayjsCustomParseFormatPlugin from 'dayjs/plugin/customParseFormat.js';
import dayjsTimezonePlugin from 'dayjs/plugin/timezone.js';
import dayjsUTCPlugin from 'dayjs/plugin/utc.js';
import { isBookedReservation } from './reservation-lifecycle.ts';
import { trimToNull } from './strings.ts';
import { parseTimeTo24h } from './time.ts';

dayjs.extend(dayjsCustomParseFormatPlugin);
dayjs.extend(dayjsUTCPlugin);
dayjs.extend(dayjsTimezonePlugin);

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** `value` is a real calendar day in YYYY-MM-DD (not `2027-02-31`, not free text). */
export const isValidIsoDate = (value: string): boolean => {
  if (!ISO_DATE_RE.test(value)) {
    return false;
  }

  const parsed = dayjs(value, 'YYYY-MM-DD', true);

  return parsed.isValid() && parsed.format('YYYY-MM-DD') === value;
};

export const DEFAULT_EVENT_START_TIME = '12:00:00';

/** Subset of shortlist / general-dining `availabilityInput` date + startTime. */
export type EventDateParams = {
  date: string;
  startTime: string;
};

type ReservationDateTimeFields = {
  date: string;
  time: string;
};

export const normalizeEventStartTime = (time: string): string | null =>
  parseTimeTo24h(time, { allowNoMinutes: true, allowNoonMidnight: true });

export const getEventDateParams = (params: {
  date: string;
  time?: string | null;
}): EventDateParams => ({
  date: params.date,
  startTime: params.time ?? DEFAULT_EVENT_START_TIME,
});

/**
 * Today's calendar date (YYYY-MM-DD) as seen in the event's timezone, falling back to UTC when
 * the event has no timezone recorded. Shared so a UI's "past date" guard (e.g. a date picker's
 * `min`) uses the same day boundary as the server-side rejection for the same event.
 */
export const todayInEventTimezone = (eventTimezone: string | null | undefined): string => {
  const tz = trimToNull(eventTimezone);
  if (!tz) {
    return dayjs.utc().format('YYYY-MM-DD');
  }

  try {
    return dayjs().tz(tz).format('YYYY-MM-DD');
  } catch {
    return dayjs.utc().format('YYYY-MM-DD');
  }
};

/** Event calendar date as YYYY-MM-DD in the event timezone (mirrors preference-date ISO normalization). */
export const getEventDateIso = (
  eventDate: string | null | undefined,
  eventTimezone: string | null | undefined,
): string | null => {
  const start = trimToNull(eventDate);
  if (start == null) {
    return null;
  }

  const tz = trimToNull(eventTimezone) ?? 'UTC';
  const base = dayjs.tz(start, tz);
  if (!base.isValid()) {
    return null;
  }

  return base.format('YYYY-MM-DD');
};

/** Event slot for offer grouping: event date + time only (no booked override). */
export const resolveEventSlotDateParams = (params: {
  eventDate?: string | null;
  eventTime?: string | null;
  eventTimezone?: string | null;
}): EventDateParams | null => {
  const date = getEventDateIso(params.eventDate, params.eventTimezone);
  if (date == null) {
    return null;
  }

  const time = trimToNull(params.eventTime) ?? undefined;

  return getEventDateParams({ date, time });
};

/** Event slot for availability / inquiry: booked reservation overrides event date + time. */
export const resolveBookedOrEventSlotDateParams = (params: {
  eventDate?: string | null;
  eventTime?: string | null;
  bookedDate?: string | null;
  bookedTime?: string | null;
}): EventDateParams | null => {
  const date = trimToNull(params.bookedDate) ?? trimToNull(params.eventDate);
  if (date == null) {
    return null;
  }

  const time = trimToNull(params.bookedTime) ?? trimToNull(params.eventTime) ?? undefined;

  return getEventDateParams({ date, time });
};

export const getEventDateParamsFromBookedOrEvent = (params: {
  eventDate: string;
  eventTime?: string | null;
  bookedDate?: string | null;
  bookedTime?: string | null;
}): EventDateParams => {
  const resolved = resolveBookedOrEventSlotDateParams(params);
  if (resolved != null) {
    return resolved;
  }

  return getEventDateParams({ date: params.eventDate, time: params.eventTime });
};

export const reservationMatchesEventDate = (
  reservation: Pick<ReservationDateTimeFields, 'date'>,
  eventDateParams: EventDateParams,
): boolean => reservation.date === eventDateParams.date;

const reservationMatchesEventTime = (
  reservation: Pick<ReservationDateTimeFields, 'time'>,
  eventDateParams: EventDateParams,
): boolean => {
  const reservationTime = normalizeEventStartTime(reservation.time);
  const eventTime = normalizeEventStartTime(eventDateParams.startTime);
  if (reservationTime == null || eventTime == null) {
    return reservation.time === eventDateParams.startTime;
  }

  return reservationTime === eventTime;
};

export const reservationMatchesEventDateParams = (
  reservation: ReservationDateTimeFields,
  eventDateParams: EventDateParams,
): boolean =>
  reservationMatchesEventDate(reservation, eventDateParams) &&
  reservationMatchesEventTime(reservation, eventDateParams);

/** Event preference date range as stored on the event (GraphQL nullable fields). */
export type EventPreferenceDateInput =
  | { start?: string | null; end?: string | null }
  | null
  | undefined;

export type EventDateRange = { start: string; end: string };

/**
 * The slot the booker asked for: event date + time, plus any other dates they said work
 * (CXE-1146). A single-day preference has no acceptable dates beyond the event date.
 * `event` is kept separate so exact-slot checks must name it explicitly.
 */
export type RequestedEventSlot = {
  event: EventDateParams;
  acceptableDates: EventDateRange | null;
};

/**
 * The booker's span of acceptable dates: both ends real YYYY-MM-DD calendar dates, start before
 * end. Anything else is no range, so free text never widens which days count as requested and
 * never reaches agent instructions (PDR-1508).
 */
export const resolveAcceptableDateRange = (
  preferenceDate: EventPreferenceDateInput,
): EventDateRange | null => {
  const start = trimToNull(preferenceDate?.start);
  const end = trimToNull(preferenceDate?.end);
  if (start == null || end == null || !isValidIsoDate(start) || !isValidIsoDate(end)) {
    return null;
  }

  return end > start ? { start, end } : null;
};

/**
 * The booker's preference dates while nothing on the event is booked. Once a reservation is booked,
 * its day is the event, so the range no longer widens which days count as requested (CXE-1146,
 * AGI-223).
 */
export const acceptablePreferenceDate = (
  preferenceDate: EventPreferenceDateInput,
  eventReservations: ReadonlyArray<{ status: string }>,
): EventPreferenceDateInput =>
  eventReservations.some(reservation => isBookedReservation(reservation.status))
    ? null
    : preferenceDate;

/** The booker named a span of acceptable dates (start before end), not a single day (PDR-1508). */
export const hasAcceptableDateRange = (preferenceDate: EventPreferenceDateInput): boolean =>
  resolveAcceptableDateRange(preferenceDate) != null;

const dateIsInRange = (date: string, range: EventDateRange | null): boolean =>
  range != null && date >= range.start && date <= range.end;

/**
 * `date` is a real YYYY-MM-DD day inside the booker's acceptable date span, ends included. Any
 * other string (venue text that sorts between the ends) is outside it.
 */
export const isDateWithinAcceptableRange = (
  date: string | null | undefined,
  preferenceDate: EventPreferenceDateInput,
): boolean => {
  const day = trimToNull(date);

  return (
    day != null &&
    isValidIsoDate(day) &&
    dateIsInRange(day, resolveAcceptableDateRange(preferenceDate))
  );
};

export const toRequestedEventSlot = (
  eventDateParams: EventDateParams,
  preferenceDate: EventPreferenceDateInput,
): RequestedEventSlot => ({
  event: eventDateParams,
  acceptableDates: resolveAcceptableDateRange(preferenceDate),
});

/** Requested slot for offer grouping: event date + time and the booker's preference date range. */
export const resolveRequestedEventSlot = (params: {
  eventDate?: string | null;
  eventTime?: string | null;
  eventTimezone?: string | null;
  preferenceDate: EventPreferenceDateInput;
}): RequestedEventSlot | null => {
  const eventDateParams = resolveEventSlotDateParams(params);

  return eventDateParams == null
    ? null
    : toRequestedEventSlot(eventDateParams, params.preferenceDate);
};

/** On the event slot, or on an acceptable date at the event time. */
export const reservationMatchesRequestedSlot = (
  reservation: ReservationDateTimeFields,
  slot: RequestedEventSlot,
): boolean => {
  if (!reservationMatchesEventTime(reservation, slot.event)) {
    return false;
  }

  if (reservationMatchesEventDate(reservation, slot.event)) {
    return true;
  }

  return dateIsInRange(reservation.date, slot.acceptableDates);
};
